import { removeUndefinedProperties } from "../utils/type.utils";

/**
 * Base theme to support multiple projects.
 * This is used to define the base theme properties that can be extended by other themes.
 */
export interface BaseTheme {
  fontFamily?: string;
  fontSizeBase?: string;
  fontSizeSm?: string;
  fontSizeLg?: string;
  fontWeightBase?: string;
  labelPosition?: LabelPosition;
  button?: {
    borderRadius?: string;
  };
  input?: {
    borderRadius?: string;
    borderWidth?: string;
    color?: string;
    backgroundColor?: string;
  };
  text?: {
    submitButton?: string;
  };
}

export type LabelPosition = "floating" | "top";

/**
 * Apply base theme styles to the document
 */
export function applyBaseTheme(theme?: BaseTheme) {
  console.debug("Applying base theme", theme);
  if (theme) {
    applyThemeOptionAsCSSVar(theme.fontFamily, "--theme-font-family");
    applyThemeOptionAsCSSVar(theme.fontSizeBase, "--theme-font-size-base");
    applyThemeOptionAsCSSVar(theme.fontSizeSm, "--theme-font-size-sm");
    applyThemeOptionAsCSSVar(theme.fontSizeLg, "--theme-font-size-lg");

    const normalizedWeight = normalizeFontWeightForCSS(theme.fontWeightBase);
    applyThemeOptionAsCSSVar(normalizedWeight, "--theme-font-weight-base");

    // Apply border options
    applyThemeOptionAsCSSVar(theme.input?.borderRadius, "--theme-input-border-radius");
    applyThemeOptionAsCSSVar(theme.button?.borderRadius, "--theme-button-border-radius");
    applyThemeOptionAsCSSVar(theme.input?.borderWidth, "--theme-input-border-width");

    // Apply input color options
    applyThemeOptionAsCSSVar(theme.input?.color, "--theme-input-color");
    applyThemeOptionAsCSSVar(theme.input?.backgroundColor, "--theme-input-background-color");
  }
}

function applyThemeOptionAsCSSVar(value: string | undefined, cssVar: string) {
  if (value) {
    document.documentElement.style.setProperty(cssVar, value);
  }
}

export function validateTheme(theme: unknown): BaseTheme {
  if (!theme) {
    theme = {};
  }
  if (typeof theme !== "object") {
    console.log("Warning: theme must be an object");
    theme = {};
  }

  const baseTheme = theme as BaseTheme;

  const sanitisedTheme: BaseTheme = {
    fontFamily: validateFontFamily(baseTheme.fontFamily),
    fontSizeBase: validateFontSize(baseTheme.fontSizeBase),
    fontSizeSm: validateFontSize(baseTheme.fontSizeSm),
    fontSizeLg: validateFontSize(baseTheme.fontSizeLg),
    fontWeightBase: validateFontWeight(baseTheme.fontWeightBase),
    labelPosition: validateLabelPosition(baseTheme.labelPosition),
    button: { borderRadius: validateBorderRadius(baseTheme.button?.borderRadius) },
    input: {
      borderRadius: validateBorderRadius(baseTheme.input?.borderRadius),
      borderWidth: validateBorderWidth(baseTheme.input?.borderWidth),
      color: validateColor(baseTheme.input?.color),
      backgroundColor: validateColor(baseTheme.input?.backgroundColor),
    },
    text: {
      submitButton: validateText(baseTheme.text?.submitButton, "submitButton"),
    },
  };

  removeUndefinedProperties(sanitisedTheme);

  return sanitisedTheme;
}

const FONT_FAMILY_REGEX = /^[a-zA-Z0-9\s,'"-]+$/;
export function validateFontFamily(value: unknown): string | undefined {
  console.debug("Validating font family", value);
  if (!value) {
    return undefined;
  }
  if (typeof value !== "string") {
    console.log("Warning: Font family must be a string");
    return undefined;
  }
  if (!FONT_FAMILY_REGEX.test(value)) {
    console.log("Warning: Font family must contain only valid characters");
    return undefined;
  }
  return value;
}

const FONT_SIZE_REGEX = /^\d+(\.\d+)?(px|pt|em|rem|%|vw|vh)$/;
export function validateFontSize(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value !== "string") {
    console.log("Warning: Font size must be a string");
    return undefined;
  }
  if (!FONT_SIZE_REGEX.test(value)) {
    console.log("Warning: Font size must contain only valid characters");
    return undefined;
  }
  return value;
}

const FONT_WEIGHT_REGEX = /^(normal|bold|[1-9]00)$/i;
export function validateFontWeight(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value !== "string") {
    console.log("Warning: Font weight must be a string");
    return undefined;
  }
  if (!FONT_WEIGHT_REGEX.test(value)) {
    console.log("Warning: Font weight must be 'normal', 'bold', or a numeric value from 100-900");
    return undefined;
  }
  return value;
}

/**
 * Normalize font weight keywords to numeric values for CSS
 * CSS will handle the bold weight calculation: calc(base + 200) clamped to 900
 *
 * Business rules:
 * - 'normal' → '400'
 * - 'bold' → '700'
 * - '300' → '300'
 * - invalid → undefined
 */
function normalizeFontWeightForCSS(baseWeight?: string): string | undefined {
  if (!baseWeight) {
    return undefined;
  }

  const validatedWeight = validateFontWeight(baseWeight);
  if (!validatedWeight) {
    // Invalid input - let CSS handle the fallback
    return undefined;
  }

  // Normalize keywords to numeric values - CSS handles the rest
  switch (validatedWeight.toLowerCase()) {
    case "normal":
      return "400";
    case "bold":
      return "700";
    default:
      return validatedWeight; // Return numeric weight as-is
  }
}

export function validateLabelPosition(labelPosition: string | undefined): LabelPosition {
  if (!labelPosition) {
    return "floating";
  }
  if (typeof labelPosition !== "string") {
    console.log("Warning: labelPosition must be a string");
    return "floating";
  }
  if (labelPosition !== "floating" && labelPosition !== "top") {
    console.log("Warning: labelPosition must be 'floating' or 'top'");
    return "floating";
  }
  return labelPosition;
}

const BORDER_RADIUS_REGEX =
  /^(?:0|(?:\d+(?:\.\d+)?)(?:px|pt|%))(?:\s+(?:0|(?:\d+(?:\.\d+)?)(?:px|pt|%))){0,3}$/;
export function validateBorderRadius(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value !== "string") {
    console.log("Warning: Border radius must be a string");
    return undefined;
  }
  const trimmedValue = value.trim();
  if (trimmedValue === "0") {
    return "0";
  }
  if (!BORDER_RADIUS_REGEX.test(trimmedValue)) {
    console.log(
      "Warning: Border radius must use valid units (px, pt, %) and format (single value to 4 values)",
    );
    return undefined;
  }
  return trimmedValue;
}

const BORDER_WIDTH_REGEX =
  /^(?:0|(?:\d+(?:\.\d+)?)(?:px|pt))(?:\s+(?:0|(?:\d+(?:\.\d+)?)(?:px|pt))){0,3}$/;
export function validateBorderWidth(value: unknown): string | undefined {
  if (!value) {
    return undefined;
  }
  if (typeof value !== "string") {
    console.log("Warning: Border width must be a string");
    return undefined;
  }
  const trimmedValue = value.trim();
  if (trimmedValue === "0") {
    // "px" is require!d otherwise label is incorrectly positioned
    return "0px";
  }
  if (!BORDER_WIDTH_REGEX.test(trimmedValue)) {
    console.log(
      "Warning: Border width must use valid units (px, pt) and format (single value to 4 values)",
    );
    return undefined;
  }
  return trimmedValue;
}

export function validateText(input: unknown, optionName: string): string | undefined {
  if (input === undefined || input === null) {
    return undefined;
  }
  if (typeof input !== "string") {
    console.log(`Warning: ${optionName} must be a string`);
    return undefined;
  }
  const trimmedValue = input.trim();
  if (trimmedValue.length === 0) {
    console.log(`Warning: ${optionName} cannot be empty`);
    return undefined;
  }
  return trimmedValue;
}

// Validates CSS color values: hex, rgb(a), hsl(a), and named colors
const HEX_COLOR_REGEX = /^#([0-9a-fA-F]{3,8})$/;
const RGB_COLOR_REGEX = /^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/;
const RGBA_COLOR_REGEX = /^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*(0|1|0?\.\d+)\s*\)$/;
const HSL_COLOR_REGEX = /^hsl\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*\)$/;
const HSLA_COLOR_REGEX = /^hsla\(\s*\d+\s*,\s*\d+%\s*,\s*\d+%\s*,\s*(0|1|0?\.\d+)\s*\)$/;
const NAMED_COLOR_REGEX = /^[a-zA-Z]+$/;

export function validateColor(value: unknown, optionName = "color"): string | undefined {
  if (!value || typeof value !== "string" || value.trim().length === 0) {
    console.log(`Warning: ${optionName} must be a non-empty string`);
    return undefined;
  }
  const trimmedValue = value.trim();
  if (
    HEX_COLOR_REGEX.test(trimmedValue) ||
    RGB_COLOR_REGEX.test(trimmedValue) ||
    RGBA_COLOR_REGEX.test(trimmedValue) ||
    HSL_COLOR_REGEX.test(trimmedValue) ||
    HSLA_COLOR_REGEX.test(trimmedValue) ||
    NAMED_COLOR_REGEX.test(trimmedValue)
  ) {
    return trimmedValue;
  }
  console.log(
    `Warning: ${optionName} must be a valid CSS color (hex, rgb[a], hsl[a], or named color)`,
  );
  return undefined;
}
